import { useMutation, useQuery } from '@tanstack/react-query'
import { PhotoGalleriesFilterParams, CreatePhotoGalleryParams, UpdatePhotoGalleryParams } from '@/types/photo-gallery'
import { usePhotoGalleriesStore } from '@/store'
import { SortOrderEnum } from '@/enums'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const usePhotoGalleries = (params: PhotoGalleriesFilterParams) => {
  const fetchPhotoGalleries = usePhotoGalleriesStore(state => state.fetchPhotoGalleries)

  return useQuery({
    queryKey: ['photo-galleries', params],
    queryFn: () => fetchPhotoGalleries(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const usePhotoGallery = (id: string) => {
  const getPhotoGallery = usePhotoGalleriesStore(state => state.getPhotoGallery)

  return useQuery({
    queryKey: ['photo-gallery', id],
    queryFn: () => getPhotoGallery(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreatePhotoGallery = () => {
  const createPhotoGallery = usePhotoGalleriesStore(state => state.createPhotoGallery)

  return useMutation({
    mutationFn: (params: CreatePhotoGalleryParams) => createPhotoGallery(params),
    onSuccess: () => {}
  })
}

export const useUpdatePhotoGallery = () => {
  const updatePhotoGallery = usePhotoGalleriesStore(state => state.updatePhotoGallery)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdatePhotoGalleryParams }) => updatePhotoGallery(id, params),
    onSuccess: () => {}
  })
}

export const useSortPhotoGallery = () => {
  const sortPhotoGallery = usePhotoGalleriesStore(state => state.sortPhotoGallery)

  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: SortOrderEnum }) => sortPhotoGallery(id, type),
    onSuccess: () => {}
  })
}

export const useDeletePhotoGallery = () => {
  const deletePhotoGallery = usePhotoGalleriesStore(state => state.deletePhotoGallery)

  return useMutation({
    mutationFn: (id: string) => deletePhotoGallery(id),
    onSuccess: () => {}
  })
}

export const useBulkDeletePhotoGalleries = () => {
  const bulkDeletePhotoGalleries = usePhotoGalleriesStore(state => state.bulkDeletePhotoGalleries)
  const deselectAllPhotoGalleries = usePhotoGalleriesStore(state => state.deselectAllPhotoGalleries)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeletePhotoGalleries(ids),
    onSuccess: response => {
      deselectAllPhotoGalleries()
      handleBulkDeleteSuccess(response)
    }
  })
}
